import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Chip, TablePagination, TextField, InputAdornment, Button,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Alert, LinearProgress, Tooltip, Grid,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel,
  Switch, Divider, FormHelperText, SelectChangeEvent,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Quiz as QuizIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  AccessTime as TimeIcon,
  School as SchoolIcon,
  Score as ScoreIcon
} from '@mui/icons-material';
import { baseUrl, config, requestID } from "../../common/utils";
import { IonPage, IonContent } from '@ionic/react';

interface Exam {
  id: number;
  ex_id: string;
  cou_id: string;
  ex_title: string;
  ex_description: string;
  ex_time_limit: number;
  ex_questlimit_display: number;
  question_display_status: string;
  display_score_status: string;
  level: string;
  status: string;
  branch_id: number;
  business_id: number;
  created_at?: string;
  updated_at?: string;
}

interface ExamFormData {
  id?: number;
  ex_id: string;
  cou_id: string;
  ex_title: string;
  ex_description: string;
  ex_time_limit: number;
  ex_questlimit_display: number;
  question_display_status: string;
  display_score_status: string;
  level: string;
  status: string;
  branch_id: number;
  business_id: number;
}

interface Course {
  id: number;
  cou_id: string;
  cou_name: string;
}

// Static levels
const LEVELS = ['year1', 'year2', 'year3', 'year4', 'year5'];

const CbtExams: React.FC = () => {
  // State Management
  const [exams, setExams] = useState<Exam[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  // Dialog States
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState<'create' | 'edit' | 'view'>('create');
  const [currentExam, setCurrentExam] = useState<Exam | null>(null);

  // Form State
  const [formData, setFormData] = useState<ExamFormData>({
    ex_id: '',
    cou_id: '',
    ex_title: '',
    ex_description: '',
    ex_time_limit: 60, // Default 60 minutes
    ex_questlimit_display: 20, // Default 20 questions
    question_display_status: 'one',
    display_score_status: 'show',
    level: 'year1',
    status: '1',
    branch_id: 0,
    business_id: 0
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Notification States
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });

  // ================= API CALLS =================

  // Fetch all exams
  const fetchExams = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(`${baseUrl()}Cbt_Exams/index`, formData, config);
      
      if (response.data.status === true) {
        setExams(response.data.data || []);
      } else {
        showSnackbar('Failed to fetch exams', 'error');
      }
    } catch (error) {
      console.error('Error fetching exams:', error);
      showSnackbar('Network error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all courses for dropdown
  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(`${baseUrl()}Cbt_Courses/index`, formData, config);
      
      if (response.data.status === true) {
        setCourses(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      showSnackbar('Failed to load courses', 'error');
    } finally {
      setLoadingCourses(false);
    }
  };

  // Create exam
  const createExam = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setLoading(true);
    try {
      const postData = new FormData();
      postData.append("requestID", requestID());
      postData.append("cou_id", formData.cou_id);
      postData.append("ex_title", formData.ex_title);
      postData.append("ex_description", formData.ex_description);
      postData.append("ex_time_limit", formData.ex_time_limit.toString());
      postData.append("ex_questlimit_display", formData.ex_questlimit_display.toString());
      postData.append("question_display_status", formData.question_display_status);
      postData.append("display_score_status", formData.display_score_status);
      postData.append("level", formData.level);
      postData.append("status", formData.status);

      console.log('Creating exam with data:', Object.fromEntries(postData));

      const response = await axios.post(`${baseUrl()}Cbt_Exams/create`, postData, config);
      
      console.log('Create response:', response.data);
      
      if (response.data.status === true) {
        showSnackbar('Exam created successfully!', 'success');
        fetchExams();
        handleCloseDialog();
      } else {
        showSnackbar(response.data.message || 'Failed to create exam', 'error');
      }
    } catch (error: any) {
      console.error('Error creating exam:', error);
      showSnackbar(error.response?.data?.message || 'Network error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Update exam
  const updateExam = async () => {
    const errors = validateForm();
    // if (Object.keys(errors).length > 0) {
    //   setFormErrors(errors);
    //   return;
    // }
    console.log ("Updating exam:", currentExam);

    if (!currentExam?.id) {
      showSnackbar('Exam ID is required for update', 'error');
      return;
    }

    setLoading(true);
    try {
      const postData = new FormData();
      postData.append("requestID", requestID());
      postData.append("id", currentExam.id.toString());
      postData.append("cou_id", formData.cou_id);
      postData.append("ex_title", formData.ex_title);
      postData.append("ex_description", formData.ex_description);
      postData.append("ex_time_limit", formData.ex_time_limit.toString());
      postData.append("ex_questlimit_display", formData.ex_questlimit_display.toString());
      postData.append("question_display_status", formData.question_display_status);
      postData.append("display_score_status", formData.display_score_status);
      postData.append("level", formData.level);
      postData.append("status", formData.status);

      console.log('Updating exam with data:', Object.fromEntries(postData));

      const response = await axios.post(`${baseUrl()}Cbt_Exams/update`, postData, config);
      
      console.log('Update response:', response.data);
      
      if (response.data.status === true) {
        showSnackbar('Exam updated successfully!', 'success');
        fetchExams();
        handleCloseDialog();
      } else {
        showSnackbar(response.data.message || 'Failed to update exam', 'error');
      }
    } catch (error: any) {
      console.error('Error updating exam:', error);
      showSnackbar(error.response?.data?.message || 'Network error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Delete exam
  const deleteExam = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this exam? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("id", id.toString());

      const response = await axios.post(`${baseUrl()}Cbt_Exams/delete`, formData, config);
      
      if (response.data.status === true) {
        showSnackbar('Exam deleted successfully!', 'success');
        fetchExams();
      } else {
        showSnackbar(response.data.message || 'Failed to delete exam', 'error');
      }
    } catch (error) {
      console.error('Error deleting exam:', error);
      showSnackbar('Network error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  // View exam details
  const viewExam = async (id: number) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("id", id.toString());

      const response = await axios.post(`${baseUrl()}Cbt_Exams/show`, formData, config);
      
      if (response.data.status === true && response.data.data) {
        const examData = response.data.data;
        setCurrentExam(examData);
        setFormData({
          id: examData.id,
          ex_id: examData.ex_id,
          cou_id: examData.cou_id,
          ex_title: examData.ex_title,
          ex_description: examData.ex_description,
          ex_time_limit: examData.ex_time_limit,
          ex_questlimit_display: examData.ex_questlimit_display,
          question_display_status: examData.question_display_status,
          display_score_status: examData.display_score_status || 'show',
          level: examData.level,
          status: examData.status,
          branch_id: examData.branch_id,
          business_id: examData.business_id
        });
        setDialogType('view');
        setOpenDialog(true);
      } else {
        showSnackbar('Exam not found', 'error');
      }
    } catch (error) {
      console.error('Error fetching exam details:', error);
      showSnackbar('Network error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ================= HELPER FUNCTIONS =================

  const validateForm = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    
    if (!formData.cou_id.trim()) {
      errors.cou_id = 'Course is required';
    }

    if (!formData.ex_title.trim()) {
      errors.ex_title = 'Exam title is required';
    } else if (formData.ex_title.length < 3) {
      errors.ex_title = 'Exam title must be at least 3 characters';
    }

    if (formData.ex_time_limit <= 0) {
      errors.ex_time_limit = 'Time limit must be greater than 0';
    }

    if (formData.ex_questlimit_display <= 0) {
      errors.ex_questlimit_display = 'Question limit must be greater than 0';
    }

    if (!formData.level) {
      errors.level = 'Level is required';
    }

    if (!formData.status) {
      errors.status = 'Status is required';
    }

    return errors;
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDialog = (type: 'create' | 'edit' | 'view', exam?: Exam) => {

    console.log("Type & Exam", type, exam);
    setDialogType(type);
    
    if (exam) {
      setCurrentExam(exam);
      setFormData({
        id: exam.id,
        ex_id: exam.ex_id,
        cou_id: exam.cou_id,
        ex_title: exam.ex_title,
        ex_description: exam.ex_description,
        ex_time_limit: exam.ex_time_limit,
        ex_questlimit_display: exam.ex_questlimit_display,
        question_display_status: exam.question_display_status,
        display_score_status: exam.display_score_status || 'show',
        level: exam.level,
        status: exam.status,
        branch_id: exam.branch_id,
        business_id: exam.business_id
      });
    } else {
      // Generate new exam ID for creation
      const newExId = `ex_id_${Date.now()}_${Math.floor(Math.random() * 1000000)}`;
      setFormData(prev => ({
        ...prev,
        ex_id: newExId,
        cou_id: '',
        ex_title: '',
        ex_description: '',
        ex_time_limit: 60,
        ex_questlimit_display: 20,
        question_display_status: 'one',
        display_score_status: 'show',
        level: 'year1',
        status: '1'
      }));
      setCurrentExam(null);
    }
    
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentExam(null);
    setFormErrors({});
  };

  const handleSubmit = () => {
    if (dialogType === 'create') {
      createExam();
    } else if (dialogType === 'edit') {
            console.log("Submitting form for:", dialogType);

      updateExam();
    }
  };

  const handleFormChange = (field: keyof ExamFormData, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    const { name, value } = event.target;
    handleFormChange(name as keyof ExamFormData, value);
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    const value = checked ? '1' : '0';
    handleFormChange(name as keyof ExamFormData, value);
  };

  const handleScoreSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    const value = checked ? 'show' : 'hide';
    handleFormChange(name as keyof ExamFormData, value);
  };

  // Get course name by ID
  const getCourseName = (couId: string) => {
    const course = courses.find(c => c.cou_id === couId);
    return course ? course.cou_name : couId;
  };

  // Format level for display
  const formatLevel = (level: string) => {
    return level.charAt(0).toUpperCase() + level.slice(1).replace('year', ' Year ');
  };

  // Format score display status
  const formatScoreStatus = (status: string) => {
    return status === 'show' ? 'Show Score' : 'Hide Score';
  };

  // Filter exams based on search
  const filteredExams = exams.filter(exam => {
    const courseName = getCourseName(exam.cou_id).toLowerCase();
    return (
      exam.ex_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      exam.ex_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      courseName.includes(searchTerm.toLowerCase()) ||
      exam.level.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Pagination
  const visibleExams = filteredExams.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Initial fetch
  useEffect(() => {
    fetchExams();
    fetchCourses();
  }, []);

  // ================= RENDER =================

  return (
    <IonPage>
    <IonContent>
  <Box sx={{ p: 3 }}>
      {/* Header Card */}
      <Card sx={{ 
        borderRadius: 3, 
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        mb: 3,
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
      }}>
        <Box sx={{ p: 3, color: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <QuizIcon fontSize="large" />
                CBT Exams Management
              </Typography>
              <Typography variant="subtitle1" sx={{ mt: 1, opacity: 0.9 }}>
                Create and manage exams for your courses
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('create')}
              disabled={courses.length === 0}
              sx={{
                bgcolor: 'white',
                color: '#8b5cf6',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)'
                },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(255,255,255,0.3)',
                  color: 'rgba(255,255,255,0.7)'
                }
              }}
            >
              New Exam
            </Button>
          </Box>
          
          <Grid container spacing={2} sx={{ mt: 3 }}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search exams by title, ID, course, or level..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'white' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    color: 'white',
                    '&::placeholder': { color: 'rgba(255,255,255,0.7)' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  fetchExams();
                  fetchCourses();
                }}
                disabled={loading}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.3)',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
          
          {courses.length === 0 && !loadingCourses && (
            <Alert 
              severity="warning" 
              sx={{ 
                mt: 2, 
                bgcolor: 'rgba(255,152,0,0.1)',
                color: 'white',
                border: '1px solid rgba(255,152,0,0.3)'
              }}
            >
              No courses found. Please create courses first before adding exams.
            </Alert>
          )}
        </Box>
      </Card>

      {/* Loading Indicator */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Exams Table */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
          <Table stickyHeader size="medium">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, width: '12%' }}>EXAM ID</TableCell>
                <TableCell sx={{ fontWeight: 700, width: '18%' }}>EXAM TITLE</TableCell>
                <TableCell sx={{ fontWeight: 700, width: '12%' }}>COURSE</TableCell>
                <TableCell sx={{ fontWeight: 700, width: '10%' }}>LEVEL</TableCell>
                <TableCell sx={{ fontWeight: 700, width: '10%' }}>STATUS</TableCell>
                <TableCell sx={{ fontWeight: 700, width: '10%' }}>SCORE DISPLAY</TableCell>
                <TableCell sx={{ fontWeight: 700, width: '8%' }}>QUESTIONS</TableCell>
                <TableCell sx={{ fontWeight: 700, width: '8%' }}>TIME</TableCell>
                <TableCell sx={{ fontWeight: 700, width: '12%' }} align="center">ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleExams.length > 0 ? (
                visibleExams.map((exam) => (
                  <TableRow key={exam.id} hover>
                    <TableCell>
                      <Chip
                        label={exam.ex_id}
                        size="small"
                        sx={{
                          bgcolor: '#e0e7ff',
                          color: '#3730a3',
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {exam.ex_title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: '200px', display: 'block' }}>
                          {exam.ex_description}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SchoolIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {getCourseName(exam.cou_id)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatLevel(exam.level)}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={Number(exam.status) === 1 ? 'Active' : 'Inactive'}
                        size="small"
                        color={Number(exam.status) === 1 ? 'success' : 'error'}
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatScoreStatus(exam.display_score_status || 'show')}
                        size="small"
                        color={exam.display_score_status === 'show' ? 'success' : 'default'}
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                        icon={<ScoreIcon fontSize="small" />}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#7c3aed' }}>
                      {exam.ex_questlimit_display}
                    </TableCell>
                    <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TimeIcon fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight={600}>
                        {exam.ex_time_limit} min
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => viewExam(exam.id)}
                            sx={{ color: '#3b82f6' }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Exam">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog('edit', exam)}
                            sx={{ color: '#10b981' }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Exam">
                          <IconButton
                            size="small"
                            onClick={() => deleteExam(exam.id)}
                            sx={{ color: '#ef4444' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {courses.length === 0 
                        ? 'No courses available. Create courses first.'
                        : searchTerm 
                          ? 'No exams found matching your search.'
                          : 'No exams found. Create your first exam.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredExams.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Card>

      {/* Exam Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          py: 2,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <QuizIcon />
            <Typography variant="h6" fontWeight={600}>
              {dialogType === 'create' && 'Create New Exam'}
              {dialogType === 'edit' && 'Edit Exam'}
              {dialogType === 'view' && 'Exam Details'}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Exam ID"
                value={formData.ex_id}
                disabled
                helperText="Auto-generated exam identifier"
                InputProps={{
                  readOnly: true,
                }}
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth error={!!formErrors.cou_id} size="small">
                <InputLabel>Course *</InputLabel>
                <Select
                  value={formData.cou_id}
                  onChange={handleSelectChange}
                  name="cou_id"
                  label="Course *"
                  disabled={dialogType === 'view'}
                >
                  {courses.map((course) => (
                    <MenuItem key={course.id} value={course.cou_id}>
                      {course.cou_name} ({course.cou_id})
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.cou_id && (
                  <FormHelperText>{formErrors.cou_id}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Exam Title *"
                value={formData.ex_title}
                onChange={(e) => handleFormChange('ex_title', e.target.value)}
                disabled={dialogType === 'view'}
                error={!!formErrors.ex_title}
                helperText={formErrors.ex_title || "Enter the exam title"}
                InputProps={{
                  readOnly: dialogType === 'view',
                }}
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.ex_description}
                onChange={(e) => handleFormChange('ex_description', e.target.value)}
                disabled={dialogType === 'view'}
                multiline
                rows={2}
                InputProps={{
                  readOnly: dialogType === 'view',
                }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!formErrors.level} size="small">
                <InputLabel>Level *</InputLabel>
                <Select
                  value={formData.level}
                  onChange={handleSelectChange}
                  name="level"
                  label="Level *"
                  disabled={dialogType === 'view'}
                >
                  {LEVELS.map((level) => (
                    <MenuItem key={level} value={level}>
                      {formatLevel(level)}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.level && (
                  <FormHelperText>{formErrors.level}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Question Display Mode</InputLabel>
                <Select
                  value={formData.question_display_status}
                  onChange={handleSelectChange}
                  name="question_display_status"
                  label="Question Display Mode"
                  disabled={dialogType === 'view'}
                >
                  <MenuItem value="one">One at a time</MenuItem>
                  <MenuItem value="all">All at once</MenuItem>
                  <MenuItem value="section">By sections</MenuItem>
                </Select>
                <FormHelperText>
                  How questions are displayed during the exam
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Time Limit (minutes) *"
                value={formData.ex_time_limit}
                onChange={(e) => handleFormChange('ex_time_limit', parseInt(e.target.value) || 0)}
                disabled={dialogType === 'view'}
                error={!!formErrors.ex_time_limit}
                helperText={formErrors.ex_time_limit || "Exam duration in minutes"}
                InputProps={{
                  readOnly: dialogType === 'view',
                  endAdornment: <InputAdornment position="end">min</InputAdornment>
                }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Questions to Display *"
                value={formData.ex_questlimit_display}
                onChange={(e) => handleFormChange('ex_questlimit_display', parseInt(e.target.value) || 0)}
                disabled={dialogType === 'view'}
                error={!!formErrors.ex_questlimit_display}
                helperText={formErrors.ex_questlimit_display || "Number of questions in exam"}
                InputProps={{
                  readOnly: dialogType === 'view',
                }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ pt: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.status === '1'}
                      onChange={handleSwitchChange}
                      name="status"
                      disabled={dialogType === 'view'}
                      color="success"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">
                        {Number(formData.status) === 1 ? 'Active' : 'Inactive'}
                      </Typography>
                      <Chip
                        label={Number(formData.status) === 1 ? 'Active' : 'Inactive'}
                        size="small"
                        color={Number(formData.status) === 1 ? 'success' : 'error'}
                        sx={{ height: 20 }}
                      />
                    </Box>
                  }
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Active exams are available to students
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ pt: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.display_score_status === 'show'}
                      onChange={handleScoreSwitchChange}
                      name="display_score_status"
                      disabled={dialogType === 'view'}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ScoreIcon fontSize="small" />
                      <Typography variant="body2">
                        {formData.display_score_status === 'show' ? 'Show Score' : 'Hide Score'}
                      </Typography>
                      <Chip
                        label={formData.display_score_status === 'show' ? 'Show' : 'Hide'}
                        size="small"
                        color={formData.display_score_status === 'show' ? 'success' : 'default'}
                        variant="outlined"
                        sx={{ height: 20 }}
                      />
                    </Box>
                  }
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Display score to students after exam completion
                </Typography>
              </Box>
            </Grid>

            {dialogType === 'view' && currentExam && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                    Additional Information
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Branch ID"
                    value={currentExam.branch_id}
                    InputProps={{ readOnly: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Business ID"
                    value={currentExam.business_id}
                    InputProps={{ readOnly: true }}
                    size="small"
                  />
                </Grid>
                {currentExam.created_at && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Created At"
                      value={new Date(currentExam.created_at).toLocaleString()}
                      InputProps={{ readOnly: true }}
                      size="small"
                    />
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: '1px solid #e2e8f0', gap: 1 }}>
          <Button 
            onClick={handleCloseDialog} 
            variant="outlined"
            color="inherit"
          >
            {dialogType === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {dialogType !== 'view' && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading || courses.length === 0}
              sx={{
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
                '&.Mui-disabled': {
                  bgcolor: 'action.disabledBackground'
                }
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Processing...
                </>
              ) : dialogType === 'create' ? 'Create Exam' : 'Update Exam'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </IonContent>
    </IonPage>
    
  
  );
};

export default CbtExams;